// ============================================================
// 八字排盘引擎 —— 基于 lunar-javascript 的 EightChar / Yun
// 独立模块：不 import 奇门引擎，互不影响
// 规则：年柱以立春为界、月柱按节气、23点起晚子时换日柱（库内制式）
// ============================================================

import { Solar } from 'lunar-javascript';
import { analyzeBazi, changshengTable, GAN_WX, ZHI_WX } from './tables.js';
import { analyzePillarRelations, tenGod } from './relations.js';
import { computeStrength } from './strength.js';

/**
 * @param {{year,month,day,hour,minute, gender:'男'|'女', name?:string}} opts
 */
export function computeBazi({ year, month, day, hour, minute = 0, gender = '男', name = '', city = '', clockText = '' }) {
  if (!year || !month || !day || hour == null) throw new Error('请提供完整的出生年月日时');
  // 注意：传入的 hour/minute 应为「用于起盘的时间」（真太阳时校正后的值）
  const solar = Solar.fromYmdHms(year, month, day, hour, minute, 0);
  const lunar = solar.getLunar();
  const ec = lunar.getEightChar();

  const base = analyzeBazi(ec);

  // 十二长生（以日干查各柱天干与地支）
  const cs = changshengTable(base.dayGan);
  base.pillars.forEach((p) => {
    p.csGan = cs[p.gan];
    p.csZhi = cs[p.zhi];
  });

  // 大运 / 流年（附十神·十二长生·冲提纲标记）
  const yun = ec.getYun(gender === '男' ? 1 : 0);
  const qiyunText = `出生后${yun.getStartYear()}年${yun.getStartMonth()}个月${yun.getStartDay()}天起运`;
  // 排运方向：阳年（甲丙戊庚壬）男顺女逆；阴年反之
  const yangYear = '甲丙戊庚壬'.includes(ec.getYearGan());
  const dayunDir = (yangYear ? (gender === '男') : (gender !== '男')) ? '顺排（阳年男／阴年女）' : '逆排（阴年男／阳年女）';
  const monthPillar = ec.getMonthGan() + ec.getMonthZhi();
  const csMap = changshengTable(base.dayGan);
  const dayun = yun.getDaYun().map((d, i) => {
    const gz = d.getGanZhi();
    const gan = gz ? gz[0] : '';
    const zhi = gz ? gz[1] : '';
    let liunian = [];
    try {
      const ln = d.getLiuNian();
      if (ln && ln.length) {
        liunian = ln.map(x => {
          const lgz = x.getGanZhi() || '';
          const lzhi = lgz[1];
          const isChong = zhi && lzhi && (
            (lzhi === '子' && zhi === '午') || (lzhi === '午' && zhi === '子') ||
            (lzhi === '丑' && zhi === '未') || (lzhi === '未' && zhi === '丑') ||
            (lzhi === '寅' && zhi === '申') || (lzhi === '申' && zhi === '寅') ||
            (lzhi === '卯' && zhi === '酉') || (lzhi === '酉' && zhi === '卯') ||
            (lzhi === '辰' && zhi === '戌') || (lzhi === '戌' && zhi === '辰') ||
            (lzhi === '巳' && zhi === '亥') || (lzhi === '亥' && zhi === '巳'));
          return {
            year: x.getYear(), age: x.getAge(), ganzhi: lgz,
            tenShen: gz && lgz && lgz[0] ? tenGod(base.dayGan, lgz[0]) : '', // 流年天干的十神（相对日主）——供应期判断
            binglin: !!gz && lgz === gz, // 岁运并临：流年与大运干支相同
            chongDayun: isChong,
          };
        });
      }
    } catch (e) { console.warn('liunian error', e.message); }
    
    const chongTigang = !!(zhi && monthPillar[1] &&
      ((monthPillar[1] === '子' && zhi === '午') || (monthPillar[1] === '午' && zhi === '子') ||
       (monthPillar[1] === '丑' && zhi === '未') || (monthPillar[1] === '未' && zhi === '丑') ||
       (monthPillar[1] === '寅' && zhi === '申') || (monthPillar[1] === '申' && zhi === '寅') ||
       (monthPillar[1] === '卯' && zhi === '酉') || (monthPillar[1] === '酉' && zhi === '卯') ||
       (monthPillar[1] === '辰' && zhi === '戌') || (monthPillar[1] === '戌' && zhi === '辰') ||
       (monthPillar[1] === '巳' && zhi === '亥') || (monthPillar[1] === '亥' && zhi === '巳')));
    return {
      idx: i,
      ganzhi: gz,
      tenShen: gan ? tenGod(base.dayGan, gan) : '',
      csZhi: zhi ? csMap[zhi] : '',
      chongTigang,
      zhiWx: zhi ? ZHI_WX[zhi] : '',
      startAge: d.getStartAge(),
      endAge: d.getEndAge(),
      startYear: d.getStartYear(),
      endYear: d.getEndYear(),
      liunian,
    };
  });

  // 公历/农历文本
  const lunarText = `${lunar.getYearInChinese()}年${lunar.getMonthInChinese()}月${lunar.getDayInChinese()}${hour >= 23 ? '（晚子时）' : ''}`;

  return {
    input: { year, month, day, hour, minute, gender, name, city },
    solarText: clockText ? `${solar.toYmd()} ${clockText}:00（出生钟表时间，排盘用真太阳时）` : solar.toYmdHms(),
    week: solar.getWeekInChinese(),
    lunarText,
    pillars: base.pillars,
    dayGan: base.dayGan,
    now: computeNowLayer(base.dayGan),
    shenSha: base.shenSha,
    wuxingCount: base.wuxingCount,
    missing: base.missing,
    taiYuan: ec.getTaiYuan(),
    dayunDir,
    strength: computeStrength({ pillars: base.pillars, dayGan: base.dayGan }),
    mingGong: ec.getMingGong(),
    shenGong: ec.getShenGong(),
    xunKongDay: ec.getDayXunKong(),
    relations: analyzePillarRelations(base.pillars),
    qiyunText,
    dayun,
  };
}

/**
 * 当下时间层 —— 「现在」的干支事实，仅供 AI 问答／批命断应期使用：
 *   流年＝立春定年（getYearInGanZhiExact）｜流月＝节气定月（getMonthGanZhiExact）｜流日＝当日日柱。
 * 只输出干支与相对日主的十神（事实层），吉凶判断交给解读模型，不在引擎里下断语。
 * @param {string} dayGan 命主日干（用于换算十神），缺省时只给干支
 */
export function computeNowLayer(dayGan = '') {
  const tg = (gz) => (gz && gz[0] && dayGan ? tenGod(dayGan, gz[0]) : '');
  try {
    const lunar = Solar.fromDate(new Date()).getLunar();
    const yearGz = String(lunar.getYearInGanZhiExact() || '');   // 立春定年
    const yueGz = String(lunar.getMonthInGanZhiExact() || '');   // 节气定月
    const riGz = String(lunar.getDayInGanZhi() || '');           // 当日日柱
    return {
      year: yearGz ? { year: new Date().getFullYear(), ganzhi: yearGz, tenShen: tg(yearGz) } : null,
      liuyue: yueGz ? { ganzhi: yueGz, tenGan: tg(yueGz) } : null,
      liuri: riGz ? { ganzhi: riGz, tenGan: tg(riGz) } : null,
      ts: Date.now(),
    };
  } catch (e) {
    console.warn('now layer error', e && e.message);
    return null;
  }
}
